/**
 * Representation Templates Service
 *
 * Provides multiple template styles for liability transfer representation text.
 * Users can choose between formal, concise, or detailed templates based on
 * their preference and the council's requirements.
 */

import type { Ticket, Hire, Driver } from '../types';

export type TemplateVariant = 'formal' | 'concise' | 'detailed';

/**
 * Generate representation text for liability transfer
 * @param ticket - The parking ticket
 * @param hire - The hire agreement
 * @param driver - The driver/hirer details
 * @param variant - Template style to use
 * @returns Formatted representation text
 */
export const generateRepresentation = (
  ticket: Ticket,
  hire: Hire,
  driver: Driver | null,
  variant: TemplateVariant = 'formal',
  companyName?: string
): string => {
  const hireCompany = companyName || 'our company';
  const hirerName = hire.driver_name || 'the hirer';
  const hirerAddress = driver?.address_line1 && driver?.postcode
    ? `${driver.address_line1}, ${driver.postcode}`
    : 'as per the attached hire agreement';
  const rawContraventionDate = ticket.contravention_date || ticket.issue_date || '';
  const parsedContraventionDate = rawContraventionDate ? new Date(rawContraventionDate) : null;
  const contraventionDate = parsedContraventionDate && !Number.isNaN(parsedContraventionDate.getTime())
    ? parsedContraventionDate.toLocaleDateString('en-GB')
    : 'the date shown on the PCN';
  const hireStart = hire.start_date
    ? new Date(hire.start_date).toLocaleDateString('en-GB')
    : 'the hire start date';
  const hireEnd = hire.end_date
    ? new Date(hire.end_date).toLocaleDateString('en-GB')
    : 'the hire end date';

  const templates: Record<TemplateVariant, string> = {
    formal: `Dear ${ticket.issuing_authority} Parking Services,

Re: Penalty Charge Notice ${ticket.pcn_number}
Vehicle Registration: ${ticket.vrn}
Date of Contravention: ${contraventionDate}

I am writing on behalf of ${hireCompany}, a vehicle hire company, to request a transfer of liability for the above Penalty Charge Notice.

At the time of the alleged contravention, the vehicle was on hire to ${hirerName} of ${hirerAddress}.

In accordance with the Road Traffic Act 1988 and the provisions of the hire agreement, the hirer has accepted full responsibility for all parking charges incurred during the hire period from ${hireStart} to ${hireEnd}.

Enclosed please find:
1. A signed hire agreement containing the liability acceptance clause
2. Proof of the hirer's acceptance of responsibility

We respectfully request that you transfer liability for this PCN to the named hirer and update your records accordingly.

Yours faithfully,
${hireCompany}`,

    concise: `To Whom It May Concern,

PCN ${ticket.pcn_number} - ${ticket.vrn}
Contravention Date: ${contraventionDate}

Vehicle was on hire to ${hirerName} (${hirerAddress}).
Hirer has accepted liability as per the attached hire agreement.

Please transfer liability accordingly.

${hireCompany}`,

    detailed: `Dear ${ticket.issuing_authority} Parking Services,

Re: Penalty Charge Notice ${ticket.pcn_number}
Vehicle Registration: ${ticket.vrn}
Date of Contravention: ${contraventionDate}

I am writing on behalf of ${hireCompany}, a vehicle hire company registered in England and Wales, to request a transfer of liability for the above Penalty Charge Notice in accordance with the relevant legislation.

DETAILS OF THE HIRE AGREEMENT

At the time of the alleged contravention on ${contraventionDate}, the vehicle ${ticket.vrn} was subject to a hire agreement with ${hirerName}, whose address is ${hirerAddress}.

The hire period was from ${hireStart} to ${hireEnd}, during which time the hirer had exclusive possession and control of the vehicle.

LEGAL BASIS FOR TRANSFER

In accordance with:
- The Road Traffic Act 1988, Schedule 23
- The Traffic Management Act 2004 (for statutory PCNs)
- The Protection of Freedoms Act 2012 (for private parking charges)

The hire agreement, a copy of which is enclosed, contains a clear liability clause whereby the hirer accepts full responsibility for all parking charges, penalties, and fines incurred during the hire period.

ENCLOSED DOCUMENTATION

Please find attached:
1. Signed hire agreement containing the liability acceptance clause
2. Proof of the hirer's signature and acceptance of terms
3. Details of the hirer including full name and address

REQUEST FOR ACTION

We respectfully request that you:
1. Transfer liability for PCN ${ticket.pcn_number} to the named hirer
2. Update your records to reflect this transfer
3. Issue any future correspondence regarding this PCN to the hirer at the address provided
4. Confirm receipt of this transfer request and the updated liability status

Should you require any additional information or documentation, please do not hesitate to contact us.

Yours faithfully,
${hireCompany}`
  };

  return templates[variant];
};

/**
 * Get template description for UI display
 */
export const getTemplateDescription = (variant: TemplateVariant): string => {
  const descriptions: Record<TemplateVariant, string> = {
    formal: 'Standard professional format. Suitable for most councils.',
    concise: 'Brief and to the point. Minimum required information.',
    detailed: 'Comprehensive with legal references. For complex cases.'
  };
  return descriptions[variant];
};

/**
 * Estimate character count for template (for preview)
 */
export const getTemplateLength = (variant: TemplateVariant): number => {
  const lengths: Record<TemplateVariant, number> = {
    formal: 800,
    concise: 250,
    detailed: 1500
  };
  return lengths[variant];
};
